import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Gift, Heart, X } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { supabase } from '@/lib/supabase';

type GiftItem = {
  id: string; name: string; description: string; image_url: string; image_urls: string[];
  price: number | null; active: boolean; stock: number;
  sort_order: number; demo?: boolean; image_position?: string;
  created_at?: string;
};

type GiftSort = 'price-desc' | 'price-asc' | 'recent' | 'oldest';

const DEMO_GIFTS: GiftItem[] = [
  ['perfume', 'Perfume especial', 'Uma fragrância delicada para guardar lembranças especiais.', 289.90],
  ['relogio', 'Smartwatch', 'Um relógio inteligente para acompanhar todos os momentos.', 549.90],
  ['fone', 'Fone sem fio', 'Música e conforto para tornar os dias ainda mais leves.', 349.90],
  ['bolsa', 'Bolsa elegante', 'Um acessório versátil e delicado para ocasiões especiais.', 399.90],
  ['camera', 'Câmera instantânea', 'Para imprimir na hora as melhores memórias com os amigos.', 649.90],
  ['mala', 'Mala de viagem', 'Pronta para acompanhar novos destinos e grandes aventuras.', 459.90],
  ['maquiagem', 'Kit de maquiagem', 'Uma seleção completa para produções cheias de personalidade.', 319.90],
  ['caixa-som', 'Caixa de som', 'Som portátil para animar encontros, viagens e comemorações.', 429.90],
  ['tenis', 'Tênis casual', 'Conforto e estilo para acompanhar a rotina da Valentina.', 379.90],
  ['joia', 'Joia delicada', 'Uma lembrança elegante para celebrar os quinze anos.', 599.90],
  ['tablet', 'Tablet', 'Para estudar, criar, assistir e se divertir onde estiver.', 1299.90],
  ['spa', 'Dia de spa', 'Um dia inteiro de cuidado, descanso e experiências relaxantes.', 450.00],
  ['livros', 'Coleção de livros', 'Novas histórias e universos para inspirar muitos capítulos.', 249.90],
  ['jantar', 'Jantar especial', 'Uma experiência gastronômica para compartilhar em família.', 390.00],
  ['decoracao', 'Decoração para o quarto', 'Detalhes charmosos para deixar o cantinho ainda mais especial.', 329.90],
].map(([id, name, description, price], index) => ({
  id: `demo-${id}`, name: String(name), description: String(description), image_url: '/presentes-demo-fotos.png', image_urls: [],
  price: Number(price), active: true, stock: 1, sort_order: index + 1, demo: true,
  image_position: `${['0%', '50%', '100%'][index % 3]} ${['0%', '25%', '50%', '75%', '100%'][Math.floor(index / 3)]}`,
}));

function GiftCard({ item, onBuy, buying }: { item: GiftItem; onBuy: (item: GiftItem) => void; buying: boolean }) {
  const images = item.image_urls?.length ? item.image_urls : [item.image_url].filter(Boolean);
  const [current, setCurrent] = useState(0);
  const change = (direction: number) => setCurrent((index) => (index + direction + images.length) % images.length);
  return <article className="valentina-gift-card">
    <div className="valentina-gift-image">
      {item.demo ? <div className="demo-product-photo" role="img" aria-label={`Foto de ${item.name}`} style={{ backgroundImage: `url(${item.image_url})`, backgroundPosition: item.image_position }} /> : images.length ? <img src={images[current]} alt={`${item.name} - imagem ${current + 1}`} /> : <Gift aria-hidden="true" />}
      {images.length > 1 && <><button className="gift-image-prev" onClick={() => change(-1)} aria-label="Imagem anterior"><ChevronLeft /></button><button className="gift-image-next" onClick={() => change(1)} aria-label="Próxima imagem"><ChevronRight /></button><div className="valentina-gift-dots">{images.map((_, index) => <button key={index} className={current === index ? 'active' : ''} onClick={() => setCurrent(index)} aria-label={`Ver imagem ${index + 1}`} />)}</div></>}
    </div>
    <div className="valentina-gift-copy">
      <h2>{item.name}</h2><p>{item.description}</p>
      {item.price != null && <strong>{Number(item.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>}
      <button className="gift-buy-button" type="button" onClick={() => onBuy(item)} disabled={buying}>{buying ? 'ABRINDO CHECKOUT...' : 'COMPRAR PRESENTE'}</button>
      <small>{item.demo ? 'Produto ilustrativo para visualização da página.' : 'Você será redirecionada ao checkout seguro para finalizar a compra.'}</small>
    </div>
  </article>;
}

export default function GiftsPage() {
  const [gifts, setGifts] = useState<GiftItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [demoNotice, setDemoNotice] = useState(false);
  const [sort, setSort] = useState<GiftSort>('price-desc');

  const load = async () => {
    const { data } = await supabase.from('valentina_gifts').select('id,name,description,image_url,image_urls,price,active,stock,sort_order,created_at').eq('active', true).gt('stock', 0).order('sort_order');
    const removedGiftNames = new Set([
      'Livro Amor Teoricamente (Capa Dura)',
      'Livro Assistente do Vilão (Edição de luxo)',
    ]);
    const available = ((data as GiftItem[]) || []).filter((item) => !removedGiftNames.has(item.name));
    setGifts(available.length ? available : DEMO_GIFTS); setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  const buyGift = async (item: GiftItem) => {
    if (item.demo) { setDemoNotice(true); return; }
    setError(''); setBuyingId(item.id);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('nuvemshop-create-checkout', {
        body: { gift_id: item.id },
      });
      if (fnError || !data?.checkout_url) throw fnError || new Error('missing checkout_url');
      window.location.href = data.checkout_url as string;
    } catch {
      setError('Não foi possível abrir o checkout agora. Tente novamente em instantes.');
      setBuyingId(null);
    }
  };

  const sortedGifts = useMemo(() => [...gifts].sort((a, b) => {
    if (sort === 'price-desc') return (b.price ?? -1) - (a.price ?? -1);
    if (sort === 'price-asc') return (a.price ?? Number.MAX_SAFE_INTEGER) - (b.price ?? Number.MAX_SAFE_INTEGER);
    const aDate = a.created_at ? new Date(a.created_at).getTime() : a.sort_order;
    const bDate = b.created_at ? new Date(b.created_at).getTime() : b.sort_order;
    return sort === 'recent' ? bDate - aDate : aDate - bDate;
  }), [gifts, sort]);

  return <main className="valentina-gifts-page section-surface">
    <div className="gifts-flower gifts-flower-top" aria-hidden="true" /><div className="gifts-flower gifts-flower-bottom" aria-hidden="true" />
    <PageHeader />
    <section className="valentina-gifts-hero"><h1>LISTA DE PRESENTES</h1><p>Sua presença já é o maior presente. Se desejar, escolha uma lembrança especial.</p><Heart size={13} fill="currentColor" /></section>
    <p className="valentina-gifts-sizes-note">Para referência, os tamanhos da Valentina são: roupas 36 ou P · calçado 36 · anéis 14 ou 16.</p>
    <div className="valentina-gifts-toolbar">
      <label htmlFor="gift-sort">Ordenar por</label>
      <select id="gift-sort" value={sort} onChange={(event) => setSort(event.target.value as GiftSort)}>
        <option value="price-desc">Maior preço</option>
        <option value="price-asc">Menor preço</option>
        <option value="recent">Mais recentes</option>
        <option value="oldest">Mais antigos</option>
      </select>
    </div>
    {error && <p className="valentina-gifts-status valentina-gifts-error">{error}</p>}
    {loading ? <p className="valentina-gifts-status">Carregando presentes...</p> : gifts.length === 0 ? <p className="valentina-gifts-status">A lista de presentes será disponibilizada em breve.</p> : <section className={`valentina-gifts-grid ${gifts.some((item) => item.demo) ? 'has-demo-gifts' : ''}`}>{sortedGifts.map((item) => <GiftCard item={item} onBuy={buyGift} buying={buyingId === item.id} key={item.id} />)}</section>}
    {demoNotice && <div className="valentina-demo-modal" role="dialog" aria-modal="true" aria-labelledby="demo-gift-title" onMouseDown={(event) => { if (event.target === event.currentTarget) setDemoNotice(false); }}><div><button type="button" onClick={() => setDemoNotice(false)} aria-label="Fechar"><X /></button><Gift aria-hidden="true" /><h2 id="demo-gift-title">Ops, sou apenas uma demonstração</h2><p>Este produto é ilustrativo e nenhuma compra foi iniciada.</p><button type="button" onClick={() => setDemoNotice(false)}>ENTENDI</button></div></div>}
  </main>;
}
